import * as pc from "playcanvas";
import { getArenaAABBs, rayHitAabb } from "@hupper/shared";
import type { GameClient } from "../net/GameClient";
import { resumeAudioContext, unlockAudioOnGesture } from "./audioContext";

/**
 * KnifeSounds — the local player's own knife audio (2D, own ears):
 *   • deploy   — equipping the knife (weapon_equip → "knife")
 *   • hit      — a swing that stabs another player (authoritative `hit`)
 *   • hitwall  — a swing that strikes geometry instead of a player
 *
 * The server doesn't report knife wall-hits, so we infer one: on each swing
 * we raycast the aim against the arena AABBs; if a wall is within reach we
 * arm a short timer, and if no player-hit lands first the wall sound plays
 * (a connecting stab clears it — the blade hit flesh, not concrete).
 */

const KNIFE_ID = "knife";
const DEPLOY_URL = "/audio/knife-deploy.mp3";
const HIT_URL = "/audio/knife-hit.mp3";
const HITWALL_URL = "/audio/knife-hitwall.mp3";
const ALL_URLS = [DEPLOY_URL, HIT_URL, HITWALL_URL];

/** Reach (m) for the hitwall raycast — short, like the melee range. */
const KNIFE_REACH = 1.8;
/** Grace window for a player-hit to pre-empt the wall sound. */
const HITWALL_DELAY_MS = 150;
const LOCAL_VOLUME = 0.5;

const slotName = (url: string): string => url.replace(/[^a-z0-9]/gi, "_");

export class KnifeSounds {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly camera: pc.Entity;
  private readonly emitter: pc.Entity;
  private readonly assets = new Map<string, pc.Asset>();
  private readonly aabbs = getArenaAABBs();
  private ready = false;
  private readonly unsubs: Array<() => void> = [];
  /** Pending wall-hit timer — cancelled if a player-hit lands first. */
  private hitwallTimer: number | null = null;

  constructor(app: pc.AppBase, client: GameClient, camera: pc.Entity) {
    this.app = app;
    this.client = client;
    this.camera = camera;

    this.emitter = new pc.Entity("knife-snd-local");
    camera.addChild(this.emitter);

    this.loadAssets();
    unlockAudioOnGesture(this.app);

    // Deploy — equipping the knife.
    this.unsubs.push(
      this.client.onWeaponEquip((msg) => {
        if (msg.sessionId === this.client.sessionId && msg.weaponId === KNIFE_ID) this.play(DEPLOY_URL);
      }),
    );

    // Hit a player — the blade connected; cancel any pending wall sound.
    this.unsubs.push(
      this.client.onHit((msg) => {
        if (msg.weaponId !== KNIFE_ID || msg.attackerSessionId !== this.client.sessionId) return;
        this.clearHitwall();
        this.play(HIT_URL);
      }),
    );

    // Swing — arm the wall sound if the aim is pointed at nearby geometry.
    this.unsubs.push(this.client.onLocalKnifeAttack(() => this.onSwing()));
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.clearHitwall();
    this.emitter.destroy();
  }

  private onSwing(): void {
    const o = this.camera.getPosition();
    const f = this.camera.forward;
    const hit = rayHitAabb(o.x, o.y, o.z, f.x, f.y, f.z, KNIFE_REACH, this.aabbs);
    if (!hit) return; // swung at open air → no wall sound
    this.clearHitwall();
    this.hitwallTimer = window.setTimeout(() => {
      this.hitwallTimer = null;
      this.play(HITWALL_URL);
    }, HITWALL_DELAY_MS);
  }

  private clearHitwall(): void {
    if (this.hitwallTimer !== null) {
      clearTimeout(this.hitwallTimer);
      this.hitwallTimer = null;
    }
  }

  private play(url: string): void {
    if (!this.ready) return;
    resumeAudioContext(this.app);
    this.emitter.sound?.play(slotName(url));
  }

  private loadAssets(): void {
    let remaining = ALL_URLS.length;
    for (const url of ALL_URLS) {
      this.app.assets.loadFromUrl(url, "audio", (err, asset) => {
        if (err || !asset) console.warn("[KnifeSounds] failed to load:", url, err);
        else this.assets.set(url, asset);
        if (--remaining === 0) this.onAssetsReady();
      });
    }
  }

  private onAssetsReady(): void {
    this.emitter.addComponent("sound");
    const s = this.emitter.sound;
    if (s) {
      s.positional = false; // 2D — own knife
      for (const url of ALL_URLS) {
        const asset = this.assets.get(url);
        if (asset) {
          s.addSlot(slotName(url), { asset: asset.id, volume: LOCAL_VOLUME, loop: false, autoPlay: false, overlap: true });
        }
      }
    }
    this.ready = true;
  }
}
